/**
 * Web Notification Eligibility Utilities
 *
 * Provides reusable functions to check if browser web notifications
 * can be displayed based on API availability, permissions, and user preferences.
 */

export type WebNotificationIneligibilityReason =
  | 'api_unavailable'
  | 'permission_not_granted'
  | 'user_disabled';

export interface WebNotificationEligibilityResult {
  canShow: boolean;
  reason?: WebNotificationIneligibilityReason;
}

/**
 * Check if the Web Notification API is available in the current environment.
 */
export const isWebNotificationApiAvailable = (): boolean => {
  return 'Notification' in window;
};

/**
 * Check if the browser has granted permission for web notifications.
 */
export const isWebNotificationPermissionGranted = (): boolean => {
  return (
    isWebNotificationApiAvailable() && Notification.permission === 'granted'
  );
};

/**
 * Check if web notifications can be displayed.
 * This consolidates all preconditions for showing a browser Notification:
 * - API availability
 * - Browser permission granted
 * - User preference enabled
 *
 * @param webNotificationsEnabled - Whether the user has enabled web notifications in settings
 * @returns Object with canShow boolean and optional reason if ineligible
 */
export const checkWebNotificationEligibility = (
  webNotificationsEnabled: boolean
): WebNotificationEligibilityResult => {
  // Check if Notification API is available
  if (!isWebNotificationApiAvailable()) {
    return { canShow: false, reason: 'api_unavailable' };
  }

  // Check browser permission
  if (Notification.permission !== 'granted') {
    return { canShow: false, reason: 'permission_not_granted' };
  }

  // Check user preference
  if (!webNotificationsEnabled) {
    return { canShow: false, reason: 'user_disabled' };
  }

  return { canShow: true };
};

/**
 * Simple boolean check if web notifications can be shown.
 * Use this when you don't need to know the specific reason for ineligibility.
 *
 * @param webNotificationsEnabled - Whether the user has enabled web notifications in settings
 * @returns true if all conditions are met to show web notifications
 */
export const canShowWebNotification = (
  webNotificationsEnabled: boolean
): boolean => {
  return (
    isWebNotificationApiAvailable() &&
    Notification.permission === 'granted' &&
    webNotificationsEnabled
  );
};
